import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { ArrowLeft, MessageSquare, Search, Upload } from 'lucide-react'
import { useContact, useContacts, useUpdateContact, type Contact, type ContactFilter, type OptInStatus } from '../api/contacts'
import { Consent, ContactTagEditor } from '../components/ContactFacts'
import { useMe } from '../api/me'
import { useTags } from '../api/tags'
import { RequestFailed } from '../api/client'
import { fullTime, initials } from '../lib/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

// Radix Select reserves the empty value, so "any" stands for no filter.
const any = 'any'

// The contacts page: the list on the left, one contact on the right. At
// phone width only one of the two shows at a time.
export function ContactsPage() {
  const { contactId } = useParams()
  return (
    <main className="grid h-full grid-cols-[minmax(0,1fr)] md:grid-cols-[minmax(300px,400px)_minmax(0,1fr)]">
      <ContactList selected={contactId} className={contactId ? 'max-md:hidden' : undefined} />
      <section aria-label="Contact" className={cn('flex min-h-0 min-w-0 flex-col overflow-y-auto', !contactId && 'max-md:hidden')}>
        {contactId ? (
          <ContactDetail key={contactId} id={contactId} />
        ) : (
          <p className="m-auto max-w-xs p-6 text-center text-muted-foreground">Choose a contact to see details and consent.</p>
        )}
      </section>
    </main>
  )
}

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function ContactList({ selected, className }: { selected: string | undefined; className: string | undefined }) {
  const me = useMe()
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<Omit<ContactFilter, 'q'>>({})
  const debounced = useDebounced(q.trim(), 250)
  const list = useContacts({ q: debounced, ...filter })
  const tags = useTags()
  const navigate = useNavigate()
  const admin = me.data?.user.role === 'owner' || me.data?.user.role === 'admin'
  const items = list.data?.pages.flatMap((p) => p.items) ?? []
  const consent = filter.suppressed ? 'suppressed' : (filter.optIn ?? any)

  return (
    <aside aria-label="Contacts" className={cn('flex min-h-0 flex-col border-r bg-card', className)}>
      <div className="flex flex-col gap-3 border-b p-4">
        <div className="flex items-center gap-2">
          <h1 className="flex-1 text-xl font-bold">Contacts</h1>
          {admin && (
            <Button asChild variant="outline" size="sm">
              <Link to="/contacts/import"><Upload className="size-4" aria-hidden="true" />Import CSV</Link>
            </Button>
          )}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Label htmlFor="contact-search" className="sr-only">Search by name or number</Label>
          <Input id="contact-search" placeholder="Name or number" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" maxLength={80} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filter.tagId ?? any} onValueChange={(v) => setFilter({ ...filter, tagId: v === any ? undefined : v })}>
            <SelectTrigger className="flex-1" aria-label="Filter by tag"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={any}>Any tag</SelectItem>
              {(tags.data ?? []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={consent}
            onValueChange={(v) =>
              setFilter({ ...filter, optIn: v === any || v === 'suppressed' ? undefined : (v as OptInStatus), suppressed: v === 'suppressed' ? true : undefined })
            }
          >
            <SelectTrigger className="flex-1" aria-label="Filter by consent"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={any}>Any consent</SelectItem>
              <SelectItem value="optedIn">Opted in</SelectItem>
              <SelectItem value="unknown">No consent recorded</SelectItem>
              <SelectItem value="optedOut">Opted out</SelectItem>
              <SelectItem value="suppressed">Do not message</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {list.isPending ? (
        <div role="status" aria-label="Loading contacts" className="flex flex-col gap-3 p-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      ) : list.isError ? (
        <p role="alert" className="m-4 rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(list.error as RequestFailed).message}</p>
      ) : items.length === 0 ? (
        <p className="m-4 rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          {debounced || filter.tagId || consent !== any ? 'No contact matches. Try another name, number or filter.' : admin ? 'No contacts yet. Customers who message you appear here, or import a CSV.' : 'No contacts yet. Customers who message you appear here.'}
        </p>
      ) : (
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                aria-current={c.id === selected ? 'page' : undefined}
                onClick={() => navigate(`/contacts/${c.id}`)}
                className={cn('flex w-full items-center gap-3 border-b px-4 py-3 text-left hover:bg-accent/60', c.id === selected && 'bg-accent')}
              >
                <span aria-hidden="true" className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">{initials(c.name)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{c.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{c.waId ? `+${c.waId}` : 'Number not shared'}</span>
                </span>
                <ConsentBadge c={c} />
              </button>
            </li>
          ))}
          {list.hasNextPage && (
            <li className="p-3">
              <Button variant="ghost" className="w-full" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                {list.isFetchingNextPage ? 'Loading' : 'Show more'}
              </Button>
            </li>
          )}
        </ul>
      )}
    </aside>
  )
}

function ConsentBadge({ c }: { c: Contact }) {
  if (c.suppressed) return <Badge variant="destructive" className="shrink-0">Do not message</Badge>
  if (c.optIn === 'optedIn') return <Badge className="shrink-0 bg-primary-soft text-primary-text">Opted in</Badge>
  if (c.optIn === 'optedOut') return <Badge variant="outline" className="shrink-0">Opted out</Badge>
  return null
}

function ContactDetail({ id }: { id: string }) {
  const contact = useContact(id)
  const me = useMe()
  if (contact.isPending || me.isPending) {
    return (
      <div role="status" aria-label="Loading the contact" className="flex flex-col gap-3 p-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }
  if (contact.isError || me.isError) {
    const err = (contact.error ?? me.error) as RequestFailed
    return (
      <div className="flex flex-col gap-3 p-4">
        <BackToContacts />
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{err.message}</p>
      </div>
    )
  }
  const admin = me.data.user.role === 'owner' || me.data.user.role === 'admin'
  return <Loaded c={contact.data} admin={admin} canTag={me.data.user.role !== 'viewer'} />
}

function BackToContacts() {
  return (
    <Button asChild variant="ghost" size="sm" className="w-fit md:hidden">
      <Link to="/contacts"><ArrowLeft className="size-4" aria-hidden="true" />Contacts</Link>
    </Button>
  )
}

function Loaded({ c, admin, canTag }: { c: Contact; admin: boolean; canTag: boolean }) {
  const update = useUpdateContact(c.id)
  const [name, setName] = useState(c.customName)
  const [editing, setEditing] = useState(false)
  const error = update.error as RequestFailed | null

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 sm:p-6">
      <BackToContacts />
      <header className="flex flex-wrap items-center gap-3">
        <span aria-hidden="true" className="flex size-12 items-center justify-center rounded-full bg-muted text-sm font-semibold">{initials(c.name)}</span>
        <div className="min-w-0 flex-1">
          {editing ? (
            <form
              className="flex flex-wrap gap-2"
              onSubmit={(e: FormEvent) => {
                e.preventDefault()
                update.mutate({ customName: name.trim() }, { onSuccess: () => setEditing(false) })
              }}
            >
              <Input aria-label="Name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} autoFocus className="min-w-0 flex-1" />
              <Button type="submit" size="sm" disabled={update.isPending}>Save</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => { setEditing(false); setName(c.customName) }}>Cancel</Button>
            </form>
          ) : (
            <>
              <h2 className="truncate text-xl font-bold">{c.name}</h2>
              <p className="text-sm text-muted-foreground">
                {c.waId ? `+${c.waId}` : 'Number not shared'}
                {c.profileName && c.customName && c.profileName !== c.customName ? `, on WhatsApp as ${c.profileName}` : ''}
              </p>
            </>
          )}
        </div>
        {!editing && admin && <Button variant="outline" size="sm" onClick={() => setEditing(true)}>{c.customName ? 'Rename' : 'Set name'}</Button>}
        {c.conversationId && (
          <Button asChild size="sm">
            <Link to={`/inbox/${c.conversationId}`}><MessageSquare className="size-4" aria-hidden="true" />Open conversation</Link>
          </Button>
        )}
      </header>

      <section className="flex flex-col gap-2 rounded-xl border bg-card p-4">
        <h3 className="text-sm font-semibold">Tags</h3>
        <ContactTagEditor c={c} canTag={canTag} />
      </section>

      <Consent c={c} admin={admin} />

      <p className="text-xs text-muted-foreground">Added {fullTime(c.createdAt)}.</p>
      {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
    </div>
  )
}
